/**
 * This hook determines whether or not a teacher may be eligible for Khanmigo.
 * We do not have enough information to determine whether or not a teacher
 * is actually eligible (e.g. they may not have provided their age) but we
 * can rule out some teachers who are definitely ineligible.
 * Returns true if maybe eligible, false if definitely ineligible.
 * */
import gql from "graphql-tag";

import {useQuery} from "@repo/data-gql";
import {gqlOp} from "@repo/data-gql-core";
import {RequestInfo} from "@repo/shared-core";

import type {
    getTeacherReferralEligibilityData as EligibilityData,
    getTeacherReferralEligibilityDataVariables as EligibilityVariables,
} from "./__graphql-types__/getTeacherReferralEligibilityData.ts";
import type {
    khanmigoForTeachersPersonalReferralCode as ReferralCodeData,
    khanmigoForTeachersPersonalReferralCodeVariables as ReferralCodeVariables,
} from "./__graphql-types__/khanmigoForTeachersPersonalReferralCode.ts";

type TeacherReferralEligibilityResult =
    | {
          status: "loading";
          data: null;
      }
    | {
          status: "error";
          data: null;
      }
    | {
          status: "success";
          data: {
              isEligible: boolean;
          };
      };

export const useTeacherReferralEligibilityQuery =
    (): TeacherReferralEligibilityResult => {
        const [queryResult] = useQuery(TEACHER_REFERRAL_ELIGIBILITY_DATA);
        // If either query is loading, return loading.
        if (queryResult.status === "loading") {
            return {status: "loading", data: null};
        }

        // If either query fails, return error.
        if (queryResult.status !== "success" || !queryResult.data.user) {
            return {status: "error", data: null};
        }

        // We may be able to support "es" and "pt" if/when the PG Khanmigo
        // sign-up flow is translated.
        const supportedLocale = RequestInfo.kaLocale === "en";
        const supportedCountry = RequestInfo.remoteCountry === "US";
        const isEligible: boolean =
            supportedLocale &&
            supportedCountry &&
            queryResult.data.user.hasAccessToAIGuideTeacher &&
            queryResult.data.user.isAIGuideEnabled;

        return {status: "success", data: {isEligible: isEligible}};
    };

export const TEACHER_REFERRAL_ELIGIBILITY_DATA = gqlOp<
    EligibilityData,
    EligibilityVariables
>(gql`
    query getTeacherReferralEligibilityData {
        user {
            id
            hasAccessToAIGuideTeacher
            isAIGuideEnabled
        }
    }
`);

export const PERSONAL_REFERRAL_CODE_QUERY = gqlOp<
    ReferralCodeData,
    ReferralCodeVariables
>(gql`
    query khanmigoForTeachersPersonalReferralCode {
        user {
            id
            khanmigoForTeachersReferralCode
        }
    }
`);
